import { canonicalJson } from './canonical-json.mjs';

/** Decode raw bytes before an ordinary JSON parser can discard duplicate keys.
 * This parses JSON structure only; semantic field/type validation follows.
 */
export function parseCanonicalJson(input, { maxDepth = 16, maxBytes = 262144 } = {}) {
  if (typeof input !== 'string' && !(input instanceof Uint8Array))
    throw new TypeError('JSON text or UTF-8 bytes required');
  const bytes = typeof input === 'string' ? new TextEncoder().encode(input) : input;
  if (bytes.length > maxBytes) throw new TypeError('JSON byte limit exceeded');
  const text = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  let offset = 0;
  const space = () => {
    while (/[\x20\t\r\n]/.test(text[offset] ?? '')) offset++;
  };
  const fail = () => {
    throw new TypeError('Invalid canonical JSON');
  };
  function string() {
    const start = offset++;
    while (offset < text.length) {
      const char = text[offset++];
      if (char === '"') return JSON.parse(text.slice(start, offset));
      if (char === '\\') offset++;
    }
    return fail();
  }
  function object(depth) {
    const result = {};
    const keys = new Set();
    offset++;
    space();
    if (text[offset] === '}') {
      offset++;
      return result;
    }
    while (offset < text.length) {
      if (text[offset] !== '"') return fail();
      const key = string();
      space();
      if (keys.has(key) || text[offset++] !== ':') return fail();
      keys.add(key);
      Object.defineProperty(result, key, {
        value: value(depth + 1),
        enumerable: true,
        writable: true,
        configurable: true,
      });
      space();
      const next = text[offset++];
      if (next === '}') return result;
      if (next !== ',') return fail();
      space();
    }
    return fail();
  }
  function array(depth) {
    const result = [];
    offset++;
    space();
    if (text[offset] === ']') {
      offset++;
      return result;
    }
    while (offset < text.length) {
      result.push(value(depth + 1));
      space();
      const next = text[offset++];
      if (next === ']') return result;
      if (next !== ',') return fail();
    }
    return fail();
  }
  function scalar() {
    const match = /^(?:true|false|null|-?(?:0|[1-9][0-9]*)(?:\.[0-9]+)?(?:[eE][+-]?[0-9]+)?)/.exec(
      text.slice(offset),
    );
    if (!match) return fail();
    offset += match[0].length;
    return JSON.parse(match[0]);
  }
  function value(depth) {
    if (depth > maxDepth) throw new TypeError('JSON depth exceeded');
    space();
    switch (text[offset]) {
      case '{':
        return object(depth);
      case '[':
        return array(depth);
      case '"':
        return string();
      default:
        return scalar();
    }
  }
  const result = value(0);
  space();
  if (offset !== text.length) return fail();
  canonicalJson(result, { maxDepth, maxBytes });
  return result;
}
